// A looped audio clip, played as Live plays it. A WARPED object on the wire
// is a clip's source file, whole, or a region render whose markers sit on it
// (clip-audio.ts `adoptWireAudio`, hydrate.ts), and Live reads a warped clip's markers as positions in that file: it plays
// from the start marker to the loop's end, then repeats the loop until the
// clip's arrangement length is used up (the model `flattenMidiClip` bakes
// into MIDI notes on the wire — one function, `playbackWindows`, states it
// for both). So object time IS marker time. An unwarped one-shot ignores
// its markers and plays the object from its top. Here that becomes the
// segments the transport, the mixdown and the stems schedule, and the
// object position a playhead inside the clip corresponds to.

import { playbackWindows, type MidiClipGeometry } from "@shared/midi/notes.js";
import { ASSERT_EPS } from "@shared/sync/canonical.js";
import type { ClipGeometry } from "@web-shared/lib/arrangement";

/** What the loop maths needs: markers plus the arrangement length. */
export type LoopGeometry = ClipGeometry & { duration: number };

/** Where a clip's object starts playing: the start marker of a warped clip
 * (a file position), the top of an unwarped one-shot. */
export function objectStart(g: ClipGeometry): number {
  return g.isWarped ? Math.max(0, g.startMarker) : 0;
}

/** One scheduled piece of a clip's object: `lengthBeats` of it, from
 * `objectOffsetBeats` in, sounding `atBeat` after the clip's start. */
export interface ObjectSegment {
  atBeat: number;
  objectOffsetBeats: number;
  lengthBeats: number;
}

/** Whether the loop brace changes what plays: warped, looping, a real range. */
export function isLooped(g: ClipGeometry): boolean {
  return g.isWarped && g.looping && g.loopEnd - g.loopStart > ASSERT_EPS;
}

/**
 * The segments of the object that sound over the clip's length, in object
 * (= marker) time. A clip that isn't looped is one segment from
 * `objectStart`; a looped one is the head [startMarker, loopEnd) then loop
 * passes, each trimmed to what is left. A marker before the object's top
 * (negative — Live never writes one) plays from the top, later.
 */
export function objectSegments(g: LoopGeometry): ObjectSegment[] {
  if (!(g.duration > 0)) return [];
  const geom: MidiClipGeometry = isLooped(g)
    ? g
    : { duration: g.duration, startMarker: objectStart(g), endMarker: g.endMarker, looping: false, loopStart: 0, loopEnd: 0 };
  const out: ObjectSegment[] = [];
  for (const w of playbackWindows(geom)) {
    const length = Math.min(w.to - w.from, g.duration - w.at);
    if (length <= ASSERT_EPS) continue;
    if (w.from >= 0) {
      out.push({ atBeat: w.at, objectOffsetBeats: w.from, lengthBeats: length });
      continue;
    }
    const missing = -w.from;
    if (length - missing <= ASSERT_EPS) continue;
    out.push({ atBeat: w.at + missing, objectOffsetBeats: 0, lengthBeats: length - missing });
  }
  return out;
}

/**
 * Where in the object the clip is at `offsetBeats` into its arrangement
 * length — for a playhead drawn over the object. Null in a gap (before a
 * clamped loop start) or past the end.
 */
export function objectPositionAt(g: LoopGeometry, offsetBeats: number): number | null {
  if (offsetBeats < 0 || offsetBeats > g.duration) return null;
  for (const seg of objectSegments(g)) {
    if (offsetBeats >= seg.atBeat - ASSERT_EPS && offsetBeats < seg.atBeat + seg.lengthBeats + ASSERT_EPS) {
      return seg.objectOffsetBeats + (offsetBeats - seg.atBeat);
    }
  }
  return null;
}
